import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { InsightActionsComponent } from '../insight-actions/insight-actions.component';
import { MobileTimezoneDatePipe } from '../../pipes/mobile-timezone-date.pipe';
import { ITag } from '../../_models/tag.model';

@Component({
  selector: 'lib-chunkprogressbar-insight',
  standalone: true,
  imports: [CommonModule, InsightActionsComponent, MobileTimezoneDatePipe],
  templateUrl: './chunkprogressbar-insight.component.html',
  styleUrls: ['./chunkprogressbar-insight.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'lib-chunkprogressbar-insight' }
})
export class ChunkprogressbarInsightComponent {
  /** Optional title to display in header row */
  @Input() title?: string;
  @Input() titleIcon?: any;
  @Input() unit?: string;

  /** Timestamp display */
  @Input() showTimestamp = false;

  /** Styling object that controls the bar appearance */
  @Input() progressCssStyle: any;
  @Input() fractionLabel: boolean = false;
  @Input() backgroundColor: string = 'lightgreen';

  /** Bar configuration */
  @Input() chunkProgressTotal = 10;
  @Input() min = 0;
  @Input() max = 10;
  @Input() value = 0;

  /** Badge toggles and visuals (kept independent of bar colors) */
  @Input() showBadge = false;
  @Input() showPill = true;
  @Input() snapToChunks = true;      // snap badge to chunk boundaries
  @Input() badgeSize = 24;           // px
  @Input() badgeBackground?: string;
  @Input() badgeTextColor?: string;
  @Input() badgeBorder?: string;
  @Input() badgeShadow?: string;

  /** If you want to override the label text; otherwise it shows this.value */
  @Input() displayValue: string | number | null = null;

  /** Optional actions block */
  @Input() showActions = false;
  @Input() actionsId = '';
  @Input() isAdmin: boolean | undefined = false;
  @Input() inBucket = false;
  @Input() tag: ITag | null = null;

  @Output() viewTagInsight = new EventEmitter<void>();
  @Output() toggleTrend = new EventEmitter<void>();

  public trackByIndex(index: number): number {
    return index;
  }

  /** Max visual chunks to prevent DOM bloat, sub-pixel rendering bugs and track overflow */
  private readonly MAX_VISUAL_CHUNKS = 50;

  /** Computed segments for discrete chunk visualization */
  get segments(): { active: boolean; color: string }[] {
    const rawTotal = Math.max(1, this.chunkProgressTotal || 10);
    const total = rawTotal > this.MAX_VISUAL_CHUNKS ? this.MAX_VISUAL_CHUNKS : rawTotal;
    const range = this.max - this.min;
    const clamped = Math.max(this.min, Math.min(this.max, this.value ?? 0));
    const ratio = range === 0 ? 0 : (clamped - this.min) / range;
    const activeCount = (this.value ?? 0) > this.min ? Math.max(1, Math.round(ratio * total)) : 0;
    const activeColor = this.backgroundColor || this.progressCssStyle?.background || 'var(--ion-color-primary, #3880ff)';
    const result: { active: boolean; color: string }[] = [];
    for (let i = 0; i < total; i++) {
      result.push({
        active: i < activeCount && (this.value ?? 0) >= this.min,
        color: activeColor
      });
    }
    return result;
  }

  /** Percent (0–100) for where to place the badge */
  get pct(): number {
    this.progressCssStyle = { 'background': this.backgroundColor };
    const range = this.max - this.min;
    const clamped = Math.max(this.min, Math.min(this.max, this.value ?? 0));
    const raw = range === 0 ? 0 : ((clamped - this.min) / range) * 100;

    if (!this.snapToChunks || this.chunkProgressTotal <= 0) {
      return +raw.toFixed(3);
    }
    const step = 100 / Math.min(this.chunkProgressTotal, this.MAX_VISUAL_CHUNKS);
    return Math.round(raw / step) * step;
  }

  /** What text appears in the badge/pill */
  get badgeLabel(): string | number {
    if (this.displayValue !== null && this.displayValue !== undefined) {
      return this.displayValue;
    }

    if (typeof this.value === 'number') {
      // If it's an integer, show as-is
      if (Number.isInteger(this.value)) {
        if (this.fractionLabel) {
          return this.getFractionLabel(this.value);
        } else {
          return this.value;
        }
      }
      // Otherwise, show with 1 decimal
      if (this.fractionLabel) {
        return this.getFractionLabel(this.value.toFixed(1) as unknown as number);
      } else {
        return this.value.toFixed(1);
      }
    }

    return this.value;
  }

  getFractionLabel(value: number): string {
    if (value <= 5) return 'E';
    if (value <= 25) return '1/4';
    if (value <= 50) return '1/2';
    if (value <= 75) return '3/4';
    return 'F';
  }

  /** Inline style object for badge */
  get badgeStyle(): Record<string, string> {
    const bg = this.fractionLabel ? this.backgroundColor : (this.badgeBackground || 'var(--chunk-badge-bg, #ffffff)');
    const border = this.fractionLabel ? `2px solid ${this.backgroundColor}` : (this.badgeBorder || 'var(--chunk-badge-border, 1.5px solid rgba(120, 130, 145, 0.3))');
    const color = this.fractionLabel ? '#ffffff' : (this.badgeTextColor || 'var(--chunk-badge-color, #0f172a)');
    const shadow = this.badgeShadow || 'var(--chunk-badge-shadow, 0 1px 4px rgba(0, 0, 0, 0.15))';
    const labelStr = String(this.badgeLabel ?? '');
    const isLongLabel = labelStr.length >= 3;

    return {
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: `${this.badgeSize}px`,
      height: `${this.badgeSize}px`,
      minWidth: `${this.badgeSize}px`,
      borderRadius: '9999px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '700',
      fontSize: isLongLabel ? '9px' : '10px',
      fontFamily: 'var(--ion-font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif)',
      background: bg,
      color: color,
      boxShadow: shadow,
      border: border,
      pointerEvents: 'none',
      zIndex: '2',
      boxSizing: 'border-box',
      lineHeight: '1',
    };
  }
}
